// useMessages hooks - Messaging functionality with React Query
// Feature: Wallet-to-wallet direct messaging

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import {
  getInbox,
  getMessages,
  getUnreadMessageCount,
  getOrStartConversation,
  sendMessage,
  sendMessageToConversation,
  markConversationAsRead,
  pollConversation,
  setTypingStatus,
  clearTypingStatus,
  type ConversationWithDetails,
  type Message,
  type Conversation,
} from '../api/messages';
import { useEffect, useRef, useCallback } from 'react';

// Query keys for cache management
export const messageQueryKeys = {
  all: ['messages'] as const,
  inbox: () => [...messageQueryKeys.all, 'inbox'] as const,
  unreadCount: () => [...messageQueryKeys.all, 'unread-count'] as const,
  conversation: (id: string) => [...messageQueryKeys.all, 'conversation', id] as const,
  conversationMessages: (id: string) => [...messageQueryKeys.all, 'conversation', id, 'messages'] as const,
  conversationWith: (address: string) => [...messageQueryKeys.all, 'with', address] as const,
};

interface UseUnreadMessageCountOptions {
  enablePolling?: boolean;
}

/**
 * Hook to get unread message count across all conversations
 * Used for navigation badge
 */
export function useUnreadMessageCount(options: UseUnreadMessageCountOptions = {}) {
  const { enablePolling = true } = options;
  const { isAuthenticated } = useAuthStore();

  const query = useQuery({
    queryKey: messageQueryKeys.unreadCount(),
    queryFn: getUnreadMessageCount,
    staleTime: 30000, // 30 seconds
    refetchInterval: enablePolling ? 30000 : false, // Poll every 30 seconds
    refetchOnWindowFocus: true,
    enabled: isAuthenticated,
  });

  return {
    ...query,
    unreadCount: query.data ?? 0,
  };
}

interface UseInboxOptions {
  limit?: number;
  offset?: number;
}

/**
 * Hook to get inbox (all conversations)
 */
export function useInbox(options: UseInboxOptions = {}) {
  const { limit = 50, offset = 0 } = options;
  const { isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: [...messageQueryKeys.inbox(), limit, offset],
    queryFn: () => getInbox(limit, offset),
    staleTime: 10000, // 10 seconds
    refetchOnWindowFocus: true,
    enabled: isAuthenticated,
  });
}

interface UseConversationMessagesOptions {
  limit?: number;
  before?: string;
  enabled?: boolean;
}

/**
 * Hook to get messages in a specific conversation
 */
export function useConversationMessages(
  conversationId: string | undefined,
  options: UseConversationMessagesOptions = {}
) {
  const { limit = 50, before, enabled = true } = options;
  const { isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: [...messageQueryKeys.conversationMessages(conversationId || ''), limit, before],
    queryFn: () => getMessages(conversationId!, limit, before),
    staleTime: 5000, // 5 seconds - messages should feel real-time
    refetchOnWindowFocus: true,
    enabled: isAuthenticated && !!conversationId && enabled,
  });
}

/**
 * Hook to get or start a conversation with a specific wallet
 */
export function useConversationWith(address: string | undefined) {
  const { isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: messageQueryKeys.conversationWith(address || ''),
    queryFn: () => getOrStartConversation(address!),
    staleTime: 60000, // 1 minute
    enabled: isAuthenticated && !!address,
  });
}

/**
 * Hook to send a new message
 * Can send to a new recipient or to an existing conversation
 */
export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      recipientAddress,
      conversationId,
      content,
    }: {
      recipientAddress?: string;
      conversationId?: string;
      content: string;
    }) => {
      if (conversationId) {
        const message = await sendMessageToConversation(conversationId, content);
        return { message, conversationId };
      } else if (recipientAddress) {
        return sendMessage(recipientAddress, content);
      } else {
        throw new Error('Either recipientAddress or conversationId is required');
      }
    },
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: messageQueryKeys.inbox() });
      queryClient.invalidateQueries({
        queryKey: messageQueryKeys.conversationMessages(data.conversationId),
      });
      queryClient.invalidateQueries({ queryKey: messageQueryKeys.unreadCount() });
    },
  });
}

/**
 * Hook to mark a conversation as read
 */
export function useMarkConversationAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markConversationAsRead,
    onSuccess: (_, conversationId) => {
      // Invalidate inbox to update unread counts
      queryClient.invalidateQueries({ queryKey: messageQueryKeys.inbox() });
      queryClient.invalidateQueries({ queryKey: messageQueryKeys.unreadCount() });
    },
  });
}

// ============================================================================
// Polling & Typing Indicator Hooks
// ============================================================================

interface UseConversationPollingOptions {
  pollingInterval?: number; // Default: 3000ms (3 seconds)
  enabled?: boolean;
}

/**
 * Hook for polling a conversation for new messages and typing status
 * Provides more responsive real-time updates than standard query refetching
 */
export function useConversationPolling(
  conversationId: string | undefined,
  options: UseConversationPollingOptions = {}
) {
  const { pollingInterval = 1000, enabled = true } = options;
  const { isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();
  const lastMessageTimeRef = useRef<string | undefined>(undefined);

  const query = useQuery({
    queryKey: [...messageQueryKeys.all, 'poll', conversationId],
    queryFn: () => pollConversation(conversationId!, lastMessageTimeRef.current),
    staleTime: pollingInterval - 500, // Slightly less than poll interval
    refetchInterval: enabled ? pollingInterval : false,
    refetchIntervalInBackground: false, // Don't poll when tab is hidden
    enabled: isAuthenticated && !!conversationId && enabled,
  });

  // When we get new messages, update the timestamp and invalidate messages query
  useEffect(() => {
    if (query.data?.hasNewMessages && query.data.latestMessage) {
      lastMessageTimeRef.current = query.data.latestMessage.created_at;
      // Invalidate messages query to fetch new messages
      queryClient.invalidateQueries({
        queryKey: messageQueryKeys.conversationMessages(conversationId!),
      });
    }
  }, [query.data, conversationId, queryClient]);

  return {
    ...query,
    typingUsers: query.data?.typingUsers ?? [],
    hasNewMessages: query.data?.hasNewMessages ?? false,
    newMessageCount: query.data?.newMessageCount ?? 0,
  };
}

interface UseTypingIndicatorOptions {
  debounceMs?: number; // Time before typing status expires (default: 3000ms)
}

/**
 * Hook for managing typing indicator status
 * Automatically sends typing status while user is typing
 * and clears it after a period of inactivity
 */
export function useTypingIndicator(
  conversationId: string | undefined,
  options: UseTypingIndicatorOptions = {}
) {
  const { debounceMs = 3000 } = options;
  const { isAuthenticated } = useAuthStore();
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTypingRef = useRef(false);

  // Send typing status
  const sendTyping = useCallback(async () => {
    if (!conversationId || !isAuthenticated) return;

    try {
      // Only send if we weren't already typing
      if (!isTypingRef.current) {
        isTypingRef.current = true;
        await setTypingStatus(conversationId);
      }

      // Clear any existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Set timeout to clear typing status after debounce period
      typingTimeoutRef.current = setTimeout(async () => {
        isTypingRef.current = false;
        try {
          await clearTypingStatus(conversationId);
        } catch {
          // Ignore errors when clearing typing status
        }
      }, debounceMs);
    } catch {
      // Ignore errors when setting typing status
    }
  }, [conversationId, isAuthenticated, debounceMs]);

  // Stop typing immediately (call when sending message)
  const stopTyping = useCallback(async () => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    isTypingRef.current = false;

    if (conversationId && isAuthenticated) {
      try {
        await clearTypingStatus(conversationId);
      } catch {
        // Ignore errors
      }
    }
  }, [conversationId, isAuthenticated]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      // Don't await cleanup, just fire and forget
      if (conversationId && isAuthenticated && isTypingRef.current) {
        clearTypingStatus(conversationId).catch(() => {});
      }
    };
  }, [conversationId, isAuthenticated]);

  return {
    sendTyping,
    stopTyping,
  };
}

// Re-export types for convenience
export type { ConversationWithDetails, Message, Conversation };
